import { useState } from 'react';
import { useMetadata } from '../../context/MetadataContext'; 
import { getValue } from '../../utils/getValue';
import { useGameState } from '../../context/GameStateContext';
import { useGlobalError } from '../../context/GlobalErrorContext';
import CountryFlag from '../../utils/CountryFlag';
import PuzzleLeaderboard from '../PuzzleLeaderboard';
import { 
  ArrowLeft,       // Back
  List,            // Leaderboard
  Play             // Start
} from 'lucide-react';


const SoloPuzzleInfo = () => {
  const { getText } = useMetadata();
  const { gameData, rejectPuzzle, startPuzzle } = useGameState();
  const { triggerError } = useGlobalError();
  const [leaderboardModalOpen, setLeaderboardModalOpen] = useState(false);

  if (!gameData?.state) {
    triggerError('high', 'SoloPuzzleInfo component rendered without puzzle data. This indicates a routing/status determination error.');
    return null;
  }

  const handleBackToFind = () => {
    rejectPuzzle();
  };

  const handleStartPuzzle = () => {
    startPuzzle();
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds === 0) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${secs}s`;
  };

  const stats = gameData?.stats || {};

  return (
    <div className="dataContainer">
      <div className="containerHeader">
        <h3>{getValue(gameData, 'messages.pre_play')}</h3>
      </div>
      
      <div className="dataContent">
        {/* Puzzle Details */}
        <div className="contentGroup">
          <div className="dataRow">
            <span className="dataLabel">{getText('common_text.style')}:</span>
            <span className="dataValue">
              {(() => {
                const v = getValue(gameData, 'puzzle.style');
                if (!v) return 'N/A';
                const displayName = v.charAt(0).toUpperCase() + v.slice(1);
                return (
                  <a
                    href={`https://gridrank.games/guides/${v.toLowerCase()}.html`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {displayName}
                  </a>
                );
              })()}
            </span>
          </div>
          
          <div className="dataRow">
            <span className="dataLabel">{getText('common_text.size')}:</span>
            <span className="dataValue">{getValue(gameData, 'puzzle.size')}</span>
          </div>
          
          <div className="dataRow">
            <span className="dataLabel">{getText('common_text.difficulty')}:</span>
            <span className="dataValue">{getValue(gameData, 'puzzle.difficulty')}</span>
          </div>
        </div>
        
        {/* Global stats - show if at least one */}
        {stats?.global_attempts > 0 && (
          <div className="contentGroup">
            <div className="dataRow">
              <span className="dataLabel">{getText('common_text.global_solves')}:</span>
              <span className="dataValue">{stats.global_solves || 0}/{stats.global_attempts || 0} ({stats.global_solve_rate || 0}%)</span>
            </div>
            
            {stats.global_leader_nickname && (
              <div className="dataRow">
                <span className="dataLabel">{getText('common_text.global_leader')}:</span>
                <span className="dataValue">
                  {stats.global_leader_nickname}
                  {stats.global_leader_country && <CountryFlag countryCode={stats.global_leader_country} />}
                </span>
              </div>
            )}
            
            <div className="dataRow">
              <span className="dataLabel">{getText('common_text.average_time')}:</span>
              <span className="dataValue">
                  {formatTime(stats.global_avg_secs)}
              </span>
            </div>
          </div>
        )}
        
        {/* Country stats - only show if there are solves
        {stats.country_solves > 0 && (
          <div className="contentGroup">
            <div className="dataRow">
              <span className="dataLabel">{getText('common_text.country_solves')}:</span>
              <span className="dataValue">{stats.country_solves}/{stats.country_attempts} ({stats.country_solve_rate}%)</span>
            </div>
            
            {stats.country_leader_nickname && (
              <div className="dataRow">
                <span className="dataLabel">{getText('common_text.country_leader')}:</span>
                <span className="dataValue">
                  {stats.country_leader_nickname}
                  {stats.country_code && <CountryFlag countryCode={stats.country_code} />}
                </span>
              </div>
            )}
            
            <div className="dataRow">
              <span className="dataLabel">{getText('common_text.total_time')}:</span>
              <span className="dataValue">
                {getText('common_text.best')}: {formatTime(stats.country_best_secs)} {getText('common_text.average')}: {formatTime(stats.country_avg_secs)}
              </span>
            </div>
          </div>
        )}
        

        {stats.age_solves > 0 && (
          <div className="contentGroup">
            <div className="dataRow">
              <span className="dataLabel">{getText('common_text.age_solves')}</span>
              <span className="dataValue">{stats.age_solves}/{stats.age_attempts} ({stats.age_solve_rate}%)</span>
            </div>
            
            {stats.age_leader_nickname && (
              <div className="dataRow">
                <span className="dataLabel">{getText('common_text.age_leader')}</span>
                <span className="dataValue">
                  {stats.age_leader_nickname}
                  {stats.age_leader_country && <CountryFlag countryCode={stats.age_leader_country} />}
                </span>
              </div>
            )}
            
            <div className="dataRow">
              <span className="dataLabel">{getText('common_text.total_time')}:</span>
              <span className="dataValue">
               {getText('common_text.best')}: {formatTime(stats.age_best_secs)} {getText('common_text.average')}: {formatTime(stats.age_avg_secs)}
              </span>
            </div>
          </div>
        )}
        

        {stats.gender_solves > 0 && (
          <div className="contentGroup">
            <div className="dataRow">
              <span className="dataLabel">{getText('common_text.gender_solves')}</span>
              <span className="dataValue">{stats.gender_solves}/{stats.gender_attempts} ({stats.gender_solve_rate}%)</span>
            </div>
            
            {stats.gender_leader_nickname && (
              <div className="dataRow">
                <span className="dataLabel">{getText('common_text.gender_leader')}</span>
                <span className="dataValue">
                  {stats.gender_leader_nickname}
                  {stats.gender_leader_country && <CountryFlag countryCode={stats.gender_leader_country} />}
                </span>
              </div>
            )}
            
            <div className="dataRow">
              <span className="dataLabel">{getText('common_text.total_time')}:</span>
              <span className="dataValue">
                {getText('common_text.best')}: {formatTime(stats.gender_best_secs)} {getText('common_text.average')}: {formatTime(stats.gender_avg_secs)}
              </span>
            </div>
          </div>
        )}*/}
      </div> 
      
      <div className="actionButtonContainer">
        <button 
          className="iconButton secondaryButton"
          onClick={handleBackToFind}
          title={getText('common_text.back')}
          aria-label={getText('common_text.back')}
        >
          <ArrowLeft className="buttonIcon" />
        </button>
        
        <button 
          className="iconButton secondaryButton"
          onClick={() => setLeaderboardModalOpen(true)}
          title={getText('common_text.leaderboard')}
          aria-label={getText('common_text.leaderboard')}
        >
          <List className="buttonIcon" />
        </button>
        
        <button 
          className="iconButton primaryButton" 
          onClick={handleStartPuzzle}
          title={getText('common_text.start')}
          aria-label={getText('common_text.start')}
        >
          <Play className="buttonIcon" />
        </button>
      </div>
      
      {/* Puzzle Leaderboard Modal */}
      <PuzzleLeaderboard
        isOpen={leaderboardModalOpen}
        onClose={() => setLeaderboardModalOpen(false)}
      />
    </div>
  );
};

export default SoloPuzzleInfo;
